/* eslint-disable */

import moment from 'moment-timezone';
import axios from 'axios';
import Vue from 'vue';
import _ from 'lodash';
import algoliasearch from 'algoliasearch';
import { collection, query, where, limit, addDoc, updateDoc, setDoc, doc, deleteDoc, orderBy, getDoc, getDocs } from 'firebase/firestore';
import { safeSnapshot } from '@/store/firestoreListen';
import { cleanAvatarUrl } from '@/utils/avatarUrl';
import { getIdToken as getFirebaseIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import { completedTodayBound, TASK_STAMP_DEFAULT_ZONE } from '@/utils/tasks/completedToday';
import { officeTimezoneOf } from '@/utils/tasks/officeTimezone';
import { searchRowsOf } from '@/utils/tasks/searchLoad';

// The office's GENERAL_TIMEZONE from state.customer.settings (New York when missing/invalid).
// ⛔ Never read rootState.settings — that module does not exist (it made every office New York
// until 2026-10-06; cypress/node-tests/tasks-office-timezone.test.js).
const officeZoneOf = rootState => officeTimezoneOf(rootState && rootState.customer && rootState.customer.settings, z => !!moment.tz.zone(z));

const searchClient = algoliasearch(process.env.VUE_APP_ALGOLIA_APP_ID, process.env.VUE_APP_ALGOLIA_API_KEY);
const tasksIndex = searchClient.initIndex('teamio-tasks');
const commentsIndex = searchClient.initIndex('teamio-task-comments');

// The Admin "Today" page's "Done today" tasks listener (subscribeCompletedTasksToday). ONE at a time.
let completedTasksTodayUnsubscribe = null;

/**
 * Stops the "Done today" tasks listener, if one is running. Returns the stopped unsubscribe
 * function (so the action can also drop it from customer.firestoreListeners), else null.
 */
export function unsubscribeCompletedTasksToday() {
    const stop = completedTasksTodayUnsubscribe;
    completedTasksTodayUnsubscribe = null;
    if (typeof stop === 'function') {
        try { stop(); } catch (err) { console.error('Error stopping the completed-today tasks listener:', err); }
        return stop;
    }
    return null;
}

/** A Firestore Timestamp / Date / string → moment, or null (one odd doc must not break the list). */
function toMomentOrNull(value) {
    if (!value) return null;
    const m = typeof value.toDate === 'function' ? moment(value.toDate()) : moment(value);
    return m.isValid() ? m : null;
}

export default {

    namespaced: true,
    state: {
        allTasksForSite: [],
        myCreatedTasks: [],
        assignedToMeTasks: [],
        myEmployeesTasks: [],
        customerAssets: [],
        taskAssets: [],
        tasksTaggedWithPatients: [],
        tasksTaggedWithPatient: [],
        task: {},
        tasksLoaded: false,
        // The Ctrl+K search's own one-time read of the open tasks (getTasksForSearch) — kept apart from the
        // lists above so the Tasks page never sees it. Rows are frozen raw docs.
        searchTasks: [],
        searchTasksLoaded: false,
        searchTasksLoading: false,
        searchTasksFailed: false,
        searchTasksSiteID: null,
        myTasksIncludesCreatedByMe: localStorage.getItem('myTasksIncludesCreatedByMe') || 'no',
        assignedToMeTasksView: localStorage.getItem('assignedToMeTasksView') || 'Show all tasks',
        createdByMeTasksView: localStorage.getItem('createdByMeTasksView') || 'Show all tasks',
        employeeTasksView: localStorage.getItem('employeeTasksView') || 'Show all tasks',
        completedTasksForSite: [],
        // The Admin "Today" page's "Done today" tasks (subscribeCompletedTasksToday): completed today, newest first.
        completedTasksToday: [],

        // Performance optimization state (similar to conversations)
        performanceFlags: {
            useOptimizedFetching: false,
            useProgressiveLoading: true,
            useAdvancedCaching: true,
            useIntelligentPreloading: false,
            enablePerformanceMonitoring: true,
            maxInitialTasks: 30,
            backgroundLoadDelay: 1000,
            preloadAdjacentCount: 10,
            enableMemoryCleanup: true,
            memoryCleanupInterval: 5 * 60 * 1000, // 5 minutes
            maxTasksPerCategory: 200
        },
        
        // Loading and performance tracking
        tasksLoadingPhase: 'idle', // 'initial', 'background', 'complete'
        tasksPerformanceMetrics: {
            initialLoadTime: 0,
            backgroundLoadTime: 0,
            totalTasksLoaded: 0,
            memoryCleanupCount: 0,
            lastCleanupTime: null
        },
        
        // Debounced update state
        pendingTaskUpdates: {
            myCreatedTasks: [],
            assignedToMeTasks: [],
            myEmployeesTasks: []
        },
        
        // NEW: Persistent tags storage
        siteTaskTags: [],
        siteTaskTagsLoaded: false,
        siteTaskTagsListener: null,
        
        // Tag synchronization status
        tagsSyncStatus: {
            needsSync: false,
            syncing: false,
            lastSyncAt: null
        }
    },

    mutations: {
        addTaskToMyCreatedTasks(state, payload) {
            let _data = [...state.myCreatedTasks];
            _data.push(payload);
            state.myCreatedTasks = _data;
        },

        addTaskToAssignedToMeTasks(state, payload) {
            let _data = [...state.assignedToMeTasks];
            _data.push(payload);
            state.assignedToMeTasks = _data;
        },

        addTaskToMyEmployeesTasks(state, payload) {
            let _data = [...state.myEmployeesTasks];
            _data.push(payload);
            state.myEmployeesTasks = _data;
        },

        resetMyCreatedTasks(state) {
            state.myCreatedTasks = [];
        },

        resetAssignedToMeTasks(state) {
            state.assignedToMeTasks = [];
        },

        resetMyEmployeesTasks(state) {
            state.myEmployeesTasks = [];
        },

        setMyEmployeesTasks(state, payload) {
            state.myEmployeesTasks = payload;
        },

        setAllTasksForSite(state, tasks) {
            state.allTasksForSite = tasks;
        },

        setTask(state, task) {
            state.task = task;
        },

        setTaskAssets(state, taskAssets) {
            state.taskAssets = taskAssets
        },

        setCustomerAssets(state, customerAssets) {
            state.customerAssets = customerAssets
        },

        setTaskComments(state, comments) {
            state.tasks.comments = comments;
        },

        setTasksLoaded(state, payload) {
            state.tasksLoaded = payload;
        },

        // The search's one-time read: start / done / failed (see getTasksForSearch).
        startSearchTasks(state) {
            state.searchTasksLoading = true;
            state.searchTasksFailed = false;
        },

        setSearchTasks(state, { siteID, rows }) {
            state.searchTasks = rows;
            state.searchTasksSiteID = siteID;
            state.searchTasksLoaded = true;
            state.searchTasksLoading = false;
            state.searchTasksFailed = false;
        },

        failSearchTasks(state) {
            state.searchTasks = [];
            state.searchTasksLoaded = false;
            state.searchTasksLoading = false;
            state.searchTasksFailed = true;
        },

        setMyTasksIncludesCreatedByMe(state, payload) {
            state.myTasksIncludesCreatedByMe = payload;
            localStorage.setItem('myTasksIncludesCreatedByMe', payload);
        },

        setAssignedToMeTasksView(state, payload) {
            state.assignedToMeTasksView = payload;
            localStorage.setItem('assignedToMeTasksView', payload);
        },

        setCreatedByMeTasksView(state, payload) {
            state.createdByMeTasksView = payload;
            if(payload !== 'Delete all completed tasks')
                localStorage.setItem('createdByMeTasksView', payload);
        },

        setEmployeeTasksView(state, payload) {
            state.employeeTasksView = payload;
            localStorage.setItem('employeeTasksView', payload);
        },

        setTasksTaggedWithPatients(state, payload) {
            state.tasksTaggedWithPatients = payload;
        },

        resetTasksTaggedWithPatients(state) {
            state.tasksTaggedWithPatients = [];
        },

        setTasksTaggedWithPatient(state, payload) {
            state.tasksTaggedWithPatient = payload;
        },

        resetTasksTaggedWithPatient(state) {
            state.tasksTaggedWithPatient = [];
        },

        setCompletedTasksForSite(state, tasks) {
            state.completedTasksForSite = tasks;
        },

        resetCompletedTasksForSite(state) {
            state.completedTasksForSite = [];
        },

        setCompletedTasksToday(state, tasks) {
            state.completedTasksToday = Array.isArray(tasks) ? tasks : [];
        },
        
        // Performance optimization mutations
        updatePerformanceFlag(state, { flag, value }) {
            state.performanceFlags[flag] = value;
        },
        
        updateTasksLoadingPhase(state, phase) {
            state.tasksLoadingPhase = phase;
        },
        
        updatePerformanceMetrics(state, metrics) {
            Object.assign(state.tasksPerformanceMetrics, metrics);
        },
        
        setPendingTaskUpdates(state, { category, tasks }) {
            state.pendingTaskUpdates[category] = tasks;
        },
        
        applyPendingTaskUpdates(state, category) {
            if (state.pendingTaskUpdates[category].length > 0) {
                state[category] = [...state.pendingTaskUpdates[category]];
                state.pendingTaskUpdates[category] = [];
            }
        },
        
        // Memory management mutation
        enforceTaskLimits(state) {
            const { maxTasksPerCategory } = state.performanceFlags;
            
            if (state.myCreatedTasks.length > maxTasksPerCategory) {
                state.myCreatedTasks = state.myCreatedTasks.slice(0, maxTasksPerCategory);
            }
            if (state.assignedToMeTasks.length > maxTasksPerCategory) {
                state.assignedToMeTasks = state.assignedToMeTasks.slice(0, maxTasksPerCategory);
            }
            if (state.myEmployeesTasks.length > maxTasksPerCategory) {
                state.myEmployeesTasks = state.myEmployeesTasks.slice(0, maxTasksPerCategory);
            }
            
            // Update metrics
            state.tasksPerformanceMetrics.memoryCleanupCount++;
            state.tasksPerformanceMetrics.lastCleanupTime = new Date();
        },
        
        // NEW: Persistent tags mutations
        setSiteTaskTags(state, tags) {
            state.siteTaskTags = tags;
        },
        
        setSiteTaskTagsLoaded(state, loaded) {
            state.siteTaskTagsLoaded = loaded;
        },
        
        setSiteTaskTagsListener(state, listener) {
            state.siteTaskTagsListener = listener;
        },
        
        setTagsSyncNeeded(state, needed) {
            state.tagsSyncStatus.needsSync = needed;
        },
        
        setTagsSyncing(state, syncing) {
            state.tagsSyncStatus.syncing = syncing;
        },
        
        setTagsSyncCompleted(state) {
            state.tagsSyncStatus.needsSync = false;
            state.tagsSyncStatus.syncing = false;
            state.tagsSyncStatus.lastSyncAt = new Date();
        },
        
        setTagsSyncFailed(state) {
            state.tagsSyncStatus.syncing = false;
        }
    },

    getters : {
        allMyTasksDueToday: state => {

            let allTasks = _.uniqBy([...state.myCreatedTasks, ...state.assignedToMeTasks, ...state.myEmployeesTasks], function (e) {
                return e.id;
            });

            let todaysTasks;
            todaysTasks = allTasks.filter(t => {
                return t.status === 'INCOMPLETE' && moment(t.dueAtStr).isSame(moment(), 'day');
            });
            return todaysTasks;
        },

        todaysTasksForEmployee: state => employeeID => {
            let allTasks = _.uniqBy([...state.myCreatedTasks, ...state.assignedToMeTasks, ...state.myEmployeesTasks], function (e) {
                return e.id;
            });

            return allTasks.filter(t => {
                return (t.createdByEmpID === employeeID || t.assignedToEmpID === employeeID)
                && t.status === 'INCOMPLETE'
                && (
                    moment(t.dueAtStr).isSame(moment(), 'day')
                    || t.repeatFrequency === 'Daily'
                    || (t.repeatFrequency === 'Weekly' && t.repeatOn === moment().format('ddd'))
                    || (t.repeatFrequency === 'Bi-Weekly' && t.repeatOn === moment().add(2, 'weeks').format('ddd'))
                    || (t.repeatFrequency === 'Monthly' && t.repeatOnDay === moment().date())
                    || (t.repeatFrequency === 'Yearly' && t.repeatOnMonth === moment().month+1 && t.repeatOnDay === moment().date())
                )
            })
        },

        isTaskDueToday: state => t => {
            return moment(t.dueAtStr).isSame(moment(), 'day');
        },

        getDueDate: state => t => {
            let today, days, dayINeed, m, y, day, d;
            // console.log('task', t);
            switch(t.repeatFrequency) {
                case 'Daily'    :   return moment();

                case 'Weekly'   :   today = moment().isoWeekday();
                                    days = ['Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
                                    dayINeed = days.indexOf(t.repeatOn) + 1;
                                    if(today === dayINeed)
                                        return moment();
                                    else if (today < dayINeed) {
                                        return moment().isoWeekday(dayINeed);
                                    } else {
                                        return moment().add(1, 'weeks').isoWeekday(dayINeed);
                                    }

                case 'Bi-Weekly':   today = moment().isoWeekday();
                                    days = ['Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
                                    dayINeed = days.indexOf(t.repeatOn) + 1;
                                    if(today === dayINeed)
                                        return moment();
                                    else if (today < dayINeed) {
                                        return moment().isoWeekday(dayINeed);
                                    } else {
                                        return moment().add(2, 'weeks').isoWeekday(dayINeed);
                                    }

                case 'Monthly'  :
                                    if(t.repeatOnLastDayOfMonth === 'yes') {
                                        return moment(t.dueAt).endOf('month');
                                    } else {
                                        m = moment().get('month');
                                        y = moment().get('year');
                                        day = moment().get('day');
                                        d = moment().get('date');
                                        if(t.repeatOnDay === d)
                                            return moment();
                                        else if (t.repeatOnDay > d)
                                            return moment().set('date',t.repeatOnDay);
                                        else
                                            return moment().add(1, 'month').set('date',t.repeatOnDay);
                                    }

                case 'Yearly':  m = moment().get('month');
                                y = moment().get('year');
                                if(t.repeatOnMonth && t.repeatOnLastDayOfMonth === 'yes') {
                                    let month = parseInt(t.repeatOnMonth);
                                    d = moment().set("month", month-1);
                                    if(t.repeatOnMonth < m+1)
                                        return d.endOf('month').add(1, 'year');
                                    else
                                        return d.endOf('month');
                                }
                                else if(t.repeatOnMonth && t.repeatOnDay) {
                                    if(t.repeatOnMonth < m+1)
                                        return moment(`${t.repeatOnMonth}/${t.repeatOnDay}/${y+1}`);
                                    else
                                        return moment(`${t.repeatOnMonth}/${t.repeatOnDay}/${y}`);
                                } else
                                    return moment(t.dueAtStr);
                default:        return moment(t.dueAtStr);
            }
        },

        getNextDueDate: state => t => {
            let nextDueDate;
            switch(t.repeatFrequency) {
                case 'Daily'    :  nextDueDate = moment(t.dueAtStr).add(1, 'day'); break;
                case 'Weekly'   :  nextDueDate = moment(t.dueAtStr).add(1, 'weeks'); break;
                case 'Bi-Weekly':  nextDueDate = moment(t.dueAtStr).add(2, 'weeks'); break;
                case 'Monthly'  :  if(t.repeatOnLastDayOfMonth === 'yes')
                                    nextDueDate = moment(t.dueAtStr).endOf('month').add(1, 'month');
                                  else
                                    nextDueDate = moment(t.dueAtStr).add(1, 'month');
                                  break;
                case 'Yearly'   :  if(t.repeatOnLastDayOfMonth === 'yes')
                                    nextDueDate = moment(t.dueAtStr).endOf('month').add(1, 'year');
                                  else
                                    nextDueDate = moment(t.dueAtStr).add(1, 'year');
                                  break;
                default         :  nextDueDate = moment(t.dueAtStr);
            }

            // Handle excluded days (support both old array format and new boolean format)
            const excludedDays = [];
            
            // New boolean format
            if (t.excludeSaturday) excludedDays.push('Saturday');
            if (t.excludeSunday) excludedDays.push('Sunday');
            
            // Legacy array format (for backward compatibility)
            if (t.excludeDays && Array.isArray(t.excludeDays) && t.excludeDays.length > 0) {
                excludedDays.push(...t.excludeDays);
            }
            
            if (excludedDays.length > 0) {
                // Keep adding days until we find a non-excluded day
                while (excludedDays.includes(nextDueDate.format('dddd'))) {
                    // For daily tasks, just add one day
                    if (t.repeatFrequency === 'Daily') {
                        nextDueDate.add(1, 'day');
                    } else {
                        // For other frequencies, if the next date falls on an excluded day,
                        // add one day at a time until we find a non-excluded day
                        nextDueDate.add(1, 'day');
                    }
                }
            }

            return nextDueDate;
        },

        getTasksTaggedWithPatient: state => patNum => {
            let allTasks = _.uniqBy([...state.myCreatedTasks, ...state.assignedToMeTasks, ...state.myEmployeesTasks], function (e) {
                return e.id;
            });

            return allTasks.filter(t => {
                let regex = new RegExp(`data-teamio-patientid="${patNum}"`, "gi");
                return regex.test(t.content) && t.status === 'INCOMPLETE';
            });
        },

        myCreatedTasks: state => state.myCreatedTasks || [],
        assignedToMeTasks: state => state.assignedToMeTasks || [],
        myEmployeesTasks: state => state.myEmployeesTasks || [],
    },

    actions: {
        setTasks({commit}, payload) {
            commit('setTasks', payload);
        },

        setTask({commit}, payload) {
            commit('setTask', payload);
        },

        async fetchAlgoliaSearchKey({ rootState }) {
            const idToken = await getFirebaseIdToken(rootState && rootState.getAuth ? rootState.getAuth() : null);
            if (!idToken) return null;
            const res = await axios.post('/algolia-search-key', {}, {
                crossdomain: true,
                headers: { Authorization: `Bearer ${idToken}` },
            });
            return res.data;
        },

        // NEW: Persistent tags actions
        async subscribeSiteTaskTags({ commit, rootState, dispatch }, { siteID }) {
            const db = rootState.db;
            const docRef = doc(db, 'task-tags', siteID);

            // Set up real-time listener
            const unsubscribe = safeSnapshot(docRef, async (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    commit('setSiteTaskTags', data.tags || []);
                } else {
                    // Document doesn't exist yet - this is first time for this site
                    commit('setSiteTaskTags', []);
                    commit('setTagsSyncNeeded', true);

                    // Auto-trigger sync from existing tasks
                    try {
                        await dispatch('syncTagsFromExistingTasks', { siteID });
                    } catch (error) {
                        console.error('subscribeSiteTaskTags: Error during auto-sync:', error);
                    }
                }
                commit('setSiteTaskTagsLoaded', true);
            }, {
                source: 'tasks/siteTags',
                onError: (error) => {
                    console.error('subscribeSiteTaskTags: Snapshot error:', error);
                    commit('setSiteTaskTagsLoaded', true);
                }
            });

            commit('setSiteTaskTagsListener', unsubscribe);
        },

        async syncTagsFromExistingTasks({ commit, rootState, state }, { siteID }) {
            if (state.tagsSyncStatus.syncing) {
                return;
            }

            commit('setTagsSyncing', true);

            try {
                const db = rootState.db;
                
                // Get all incomplete tasks for this site
                const tasksQuery = query(
                    collection(db, 'tasks'),
                    where('siteID', '==', siteID),
                    where('status', '==', 'INCOMPLETE')
                );
                
                const tasksSnapshot = await getDocs(tasksQuery);
                const allTags = new Set();
                
                // Collect all unique tags from tasks
                tasksSnapshot.forEach((taskDoc) => {
                    const task = taskDoc.data();
                    if (task.tags && Array.isArray(task.tags)) {
                        task.tags.forEach(tag => {
                            if (tag && typeof tag === 'string' && tag.trim()) {
                                allTags.add(tag.trim());
                            }
                        });
                    }
                });

                // Save to task-tags collection
                if (allTags.size > 0) {
                    const docRef = doc(db, 'task-tags', siteID);
                    const sortedTags = Array.from(allTags).sort();
                    
                    await setDoc(docRef, {
                        siteID: siteID,
                        tags: sortedTags,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                        syncedFromTasks: true,
                        lastSyncAt: new Date()
                    });

                    commit('setSiteTaskTags', sortedTags);
                }

                commit('setTagsSyncCompleted');
            } catch (error) {
                console.error('syncTagsFromExistingTasks: Error syncing tags:', error);
                commit('setTagsSyncFailed');
                throw error;
            } finally {
                commit('setTagsSyncing', false);
            }
        },

        async addSiteTaskTag({ rootState, dispatch }, { siteID, tagName }) {
            if (!tagName || !tagName.trim()) {
                return;
            }

            const trimmedTag = tagName.trim();
            const db = rootState.db;
            const docRef = doc(db, 'task-tags', siteID);
            
            try {
                // Get current tags
                const docSnap = await getDoc(docRef);
                
                if (!docSnap.exists()) {
                    // Document doesn't exist - might be first tag for this site
                    // Before creating, sync existing tags from tasks
                    await dispatch('syncTagsFromExistingTasks', { siteID });
                    
                    // Get the document again after sync
                    const updatedDocSnap = await getDoc(docRef);
                    const currentTags = updatedDocSnap.exists() ? (updatedDocSnap.data().tags || []) : [];
                    
                    if (!currentTags.includes(trimmedTag)) {
                        const updatedTags = [...currentTags, trimmedTag].sort();
                        await updateDoc(docRef, {
                            tags: updatedTags,
                            updatedAt: new Date()
                        });
                    }
                } else {
                    // Document exists, add tag normally
                    const currentTags = docSnap.data().tags || [];
                    
                    if (!currentTags.includes(trimmedTag)) {
                        const updatedTags = [...currentTags, trimmedTag].sort();
                        
                        await updateDoc(docRef, {
                            tags: updatedTags,
                            updatedAt: new Date()
                        });
                    }
                }
            } catch (error) {
                console.error('addSiteTaskTag: Error adding tag:', error);
                throw error;
            }
        },

        async removeSiteTaskTag({ rootState }, { siteID, tagName }) {
            const db = rootState.db;
            
            // SAFETY CHECK: Verify no active tasks use this tag
            const tasksQuery = query(
                collection(db, 'tasks'),
                where('siteID', '==', siteID),
                where('status', '==', 'INCOMPLETE'),
                where('tags', 'array-contains', tagName)
            );
            
            const tasksWithTag = await getDocs(tasksQuery);
            
            if (tasksWithTag.size > 0) {
                throw new Error(`Cannot remove tag "${tagName}" - ${tasksWithTag.size} tasks still use it`);
            }
            
            // Safe to remove
            const docRef = doc(db, 'task-tags', siteID);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const currentTags = docSnap.data().tags || [];
                const updatedTags = currentTags.filter(t => t !== tagName);
                
                await updateDoc(docRef, {
                    tags: updatedTags,
                    updatedAt: new Date()
                });
            }
        },

        unsubscribeSiteTaskTags({ state, commit }) {
            if (state.siteTaskTagsListener) {
                state.siteTaskTagsListener();
                commit('setSiteTaskTagsListener', null);
            }
        },

        async getTaskReports({commit, rootState, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(tasksRef, where('siteID', '==', payload.siteID));
                const unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    const tasks = [];
                    tasksSnapshot.forEach((task) => {
                        const tempTask = task.data();
                        tempTask.id = task.id;
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';
                        tasks.push(tempTask);
                    });
                    commit('setAllTasksForSite', tasks);
                }, { source: 'tasks/reports' });
                commit('customer/addFirestoreListener', unsubscribe, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
            } catch (error) {
                console.log(error);
            }
        },

        async addTaskComment({commit, rootState, dispatch}, payload) {
            // Destructure payload for clarity
            const { comment, assignees = [], taskID, taskContent, commenterID, siteID } = payload;
            // console.log(`addTaskComment called with:`, { taskID, commenterID, assignees });

            try {
                // 1. Save the comment
                const commentRef = await addDoc(collection(rootState.db, 'comments'), comment);
                // console.log(`Comment ${commentRef.id} saved for task ${taskID}`);

                // 2. Add comment to Algolia (non-blocking - comment is saved even if Algolia fails)
                try {
                    let commentSearchRecord = {
                        ...comment,
                        objectID: commentRef.id
                    };
                    await commentsIndex.saveObject(commentSearchRecord);
                    // console.log(`Comment ${commentRef.id} added to Algolia index.`);
                } catch (algoliaErr) {
                    console.warn('Algolia: Error indexing comment (non-critical):', algoliaErr.message);
                }

                // 2b. Push the comment to the patient's Open Dental commlog when the user asked for it.
                // This lived in updateTaskWithComment until bce2d042 (2025-03-31) split that action into
                // updateTask + addTaskComment and dropped the call, which silently broke the feature:
                // the flag kept riding along on the Firestore doc with nothing left to read it.
                // Non-blocking by design — the comment is already saved, so a commlog failure must
                // report itself but never roll the comment back.
                if (comment && comment.saveToCommLog === 'TRUE') {
                    try {
                        const taggedPatients = comment.taggedPatients || [];
                        // TodoDetails blocks the save unless exactly one patient is tagged; re-assert it
                        // here so a future caller of this action can't enqueue a commlog row for the
                        // wrong patient (or for no patient at all).
                        if (taggedPatients.length !== 1) {
                            throw new Error(`commlog needs exactly one tagged patient, got ${taggedPatients.length}`);
                        }
                        // Without defaultDB the handler builds an "undefined.commlog" INSERT that only
                        // fails at the far end of the SQS queue, inside the on-prem exe — invisible here.
                        if (!comment.defaultDB) {
                            throw new Error('missing defaultDB for this user');
                        }
                        // Key spelling must match main-service task.saveTaskCommentToCommLog, which
                        // destructures lowercase {siteID, patNum, comment, defaultDB, userODNum}.
                        await axios.post('/save-task-comment-to-commlog', {
                            siteID: comment.siteID,
                            patNum: taggedPatients[0],
                            comment: comment.text,
                            defaultDB: comment.defaultDB,
                            userODNum: comment.userODNum
                        }, { crossdomain: true });
                    } catch (commLogErr) {
                        console.error('Error saving task comment to commlog:', commLogErr);
                        dispatch('alert/setErrored', true, { root: true });
                        dispatch('alert/setMessage', `Comment saved, but it could not be sent to the Open Dental commlog: ${commLogErr.message}`, { root: true });
                    }
                }

                // --- NEW LOGIC: Identify all involved users and recipients ---
                // 3. Fetch the task to get the creatorID (ensure task data is complete)
                const taskRef = doc(rootState.db, "tasks", taskID);
                const taskDoc = await getDoc(taskRef);
                if (!taskDoc.exists()) {
                    // console.warn(`Task ${taskID} not found when trying to add comment/notify.`); // Keep warns? Maybe comment out for now.
                    throw new Error(`Task ${taskID} not found.`);
                }
                const taskData = taskDoc.data();
                const creatorID = taskData.createdByEmpID;
                const currentAssignees = taskData.assignees || []; // Use assignees from fetched task data

                // 4. Determine all involved users (creator + assignees)
                // Filter null/undefined: auto-generated tasks (e.g. workflow-spawned)
                // have no human creator, so creatorID may be null. Without this guard,
                // we'd create junk notification docs with employeeID === null.
                const involvedUserIDs = [...new Set([creatorID, ...currentAssignees].filter(id => id != null))];
                // console.log(`Involved users (creator + assignees):`, involvedUserIDs);

                // 5. Determine recipients (involved users excluding the commenter)
                const recipients = involvedUserIDs.filter(id => id !== commenterID);
                // console.log(`Final recipients to notify/mark unread:`, recipients);
                // --- END NEW LOGIC ---

                // 6. Create notifications and collect recipient IDs for task update
                const notificationPromises = [];
                const recipientIDsForUnreadList = []; // Use this list for updating unreadCommentsList

                // Iterate through the final list of RECIPIENTS
                for (const recipientId of recipients) {
                    // Get commenter avatar (assuming rootState.customer.employees is populated)
                    const commenterEmployee = rootState.customer.employees?.find(emp => emp.EmployeeID === commenterID);
                    const commenterAvatarUrl = cleanAvatarUrl(commenterEmployee?.PictureUrl);

                    // --- NEW: Truncate comment text ---
                    const maxCommentLength = 75;
                    let truncatedComment = comment.text;
                    if (truncatedComment.length > maxCommentLength) {
                        truncatedComment = truncatedComment.substring(0, maxCommentLength) + "...";
                    }
                    // --- END NEW ---

                    const notification = {
                        audience: 'EMPLOYEE',
                        employeeID: recipientId, // Notify the recipient
                        type: 'TASK_COMMENT',
                        senderAvatarUrl: commenterAvatarUrl,
                        text: `${comment.createdByEmpName} commented on <a href='/app/todo/${taskID}'>task</a>: "${truncatedComment}"`,
                        siteID: siteID,
                        taskID: taskID,
                        commentID: commentRef.id,
                        createdAt: new Date(),
                        unreadList: [recipientId] // Initially unread only for this specific recipient
                    };
                    // console.log(`Preparing notification for recipient ${recipientId}:`, notification);
                    notificationPromises.push(addDoc(collection(rootState.db, 'notifications'), notification));
                    recipientIDsForUnreadList.push(recipientId); // Add to list for task update
                }

                // 7. Update the task's unreadCommentsList in Firestore
                if (recipientIDsForUnreadList.length > 0) {
                    const currentUnreadList = taskData.unreadCommentsList || [];
                    // Merge and ensure uniqueness
                    const newUnreadSet = new Set([...currentUnreadList, ...recipientIDsForUnreadList]);
                    const updatedUnreadList = Array.from(newUnreadSet);

                    // console.log(`Updating task ${taskID} unreadCommentsList to:`, updatedUnreadList);
                    // Use the existing taskRef from step 3
                    await updateDoc(taskRef, { unreadCommentsList: updatedUnreadList });
                } else {
                    // console.log(`No new recipients to add to unreadCommentsList for task ${taskID}`);
                }

                // 8. Wait for all notifications (and task update) to be saved
                await Promise.all(notificationPromises);
                // console.log(`Notifications created and task updated successfully for comment ${commentRef.id}`);

            } catch(err) {
                console.error('Error in addTaskComment action:', err);
                // Provide more specific error feedback if possible
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', `Failed to add comment or create notifications: ${err.message}`, { root: true });
                // Optionally re-throw if the calling component needs to handle it
                // throw err;
            }
        },

        // Notify people newly assigned to an EXISTING task. Creation is handled by
        // addPersonalTask; this is the update-side counterpart, which bce2d042 (2025-03-31)
        // left unwired when it split updateTaskWithComment: TodoDetails kept building the
        // notification array and then dropped it into an empty `if` block whose only body was
        // a commented-out call to a `sendNotifications` method that has never existed. So
        // re-assigning a task has been silent ever since.
        //
        // Deliberately non-blocking: the task assignment itself is already saved by the time
        // this runs, so a notification failure must report itself without undoing the save.
        async addTaskAssignmentNotifications({ rootState, dispatch }, payload) {
            const { notifications = [], taskID } = payload || {};
            if (!notifications.length || !taskID) return;

            try {
                for (const n of notifications) {
                    // Same two steps addPersonalTask performs, except the id already exists here.
                    const notification = {
                        ...n,
                        text: (n.text || '').replace('#taskID#', taskID),
                        taskID
                    };
                    await addDoc(collection(rootState.db, 'notifications'), notification);
                }
            } catch (err) {
                console.error('Error creating task assignment notifications:', err);
                dispatch('alert/setErrored', true, { root: true });
                dispatch('alert/setMessage', `Task saved, but the new assignees could not be notified: ${err.message}`, { root: true });
            }
        },

        async addPersonalTask({ commit, rootState, getters, dispatch}, payload) {
            try {
                // _preserveRepeatOnDay is a composer-side marker; it must never reach Firestore or Algolia.
                const task = { ...payload.task };
                delete task._preserveRepeatOnDay;

                const taskRef = await addDoc(collection(rootState.db, 'tasks'), task);

                if(payload.notification) {
                    // Accept an array (ToDo.vue / TaskComposer) or a single object (legacy PracticePulse callers).
                    const notifications = Array.isArray(payload.notification) ? payload.notification : [payload.notification];
                    for (let n of notifications) {
                        n.text = n.text.replace("#taskID#", taskRef.id);
                        n.taskID = taskRef.id;
                        await addDoc(collection(rootState.db, 'notifications'), n);
                    }
                }
                if(getters.isTaskDueToday(task)) {
                    localStorage.removeItem('notificationsLastCleared');
                    dispatch('notifications/setNotificationLastCleared', null, {root: true});
                }

                // Add to Algolia (non-blocking - task is created even if Algolia fails)
                try {
                    let searchRecord = {
                        ...task,
                        objectID: taskRef.id
                    };
                    await tasksIndex.saveObject(searchRecord);
                } catch (algoliaErr) {
                    console.warn('Algolia: Error indexing new task (non-critical):', algoliaErr.message);
                }
                
                // NEW: Ensure tags exist in task-tags collection
                if (task.tags && Array.isArray(task.tags) && task.siteID) {
                    for (const tag of task.tags) {
                        if (tag && tag.trim()) {
                            try {
                                await dispatch('addSiteTaskTag', {
                                    siteID: task.siteID,
                                    tagName: tag.trim() 
                                });
                            } catch (error) {
                                console.error(`Failed to add tag "${tag}" to task-tags collection:`, error);
                                // Don't fail the whole operation if tag sync fails
                            }
                        }
                    }
                }

                // Return the new task ID so caller can use it (e.g., for scroll-to-task)
                return taskRef.id;
            } catch(err) {
                console.error('Error adding a task:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async updateMultipleTasks({ commit, rootState, dispatch}, tasks) {
            try {
                const batch = rootState.db.batch();
                tasks.forEach(async function(task) {
                    const ref = doc(rootState.db, "tasks", task.id);
                    batch.update(ref, {"sortID": task.sortID});

                    // Algolia update (non-blocking)
                    try {
                        let searchRecord = {
                            ...task,
                            objectID: task.id
                        };
                        await tasksIndex.partialUpdateObject(searchRecord);
                    } catch (algoliaErr) {
                        console.warn('Algolia: Error updating task in index (non-critical):', algoliaErr.message);
                    }
                });
                await batch.commit();
            } catch(err) {
                console.error(err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async updateTask({ commit, rootState, getters, dispatch}, payload) {
            try {
                dispatch('alert/setLoading2', true, { root: true });

                // --- BEGIN MODIFICATION: Handle un-completing recurring tasks ---
                let finalPayload = { ...payload }; // Clone payload to modify safely

                // 1. Get current task state (handle potential undefined task)
                const getCurrentTask = (taskId) => {
                  const findInArray = (arr) => arr?.find(t => t.id === taskId);
                  return findInArray(rootState.tasks.myCreatedTasks) ||
                         findInArray(rootState.tasks.assignedToMeTasks) ||
                         findInArray(rootState.tasks.myEmployeesTasks);
                }
                const currentTask = getCurrentTask(payload.id);

                // 2. Check if un-completing a recurring task
                if (
                    payload.status === 'INCOMPLETE' &&
                    currentTask?.status === 'COMPLETED' &&
                    currentTask?.repeat === true &&
                    currentTask?.dueAt // Ensure currentTask.dueAt exists
                   ) {
                    // console.log(`Reverting completed recurring task: ${payload.id}`);

                    // 3. Store original completedAt (if exists) and calculate potential previous due date - REMOVED CALCULATION
                    // const originalCompletedAt = currentTask.completedAt;
                    // const currentDueDateString = currentTask.dueAt;
                    // let potentialPreviousDueDate;
                    // let useOriginalCompletedDate = false;

                    // switch(currentTask.repeatFrequency) {
                    //     case 'Daily'    :  potentialPreviousDueDate = moment(currentDueDateString).subtract(1, 'day'); break;
                    //     case 'Weekly'   :  potentialPreviousDueDate = moment(currentDueDateString).subtract(1, 'week'); break;
                    //     case 'Bi-Weekly':  potentialPreviousDueDate = moment(currentDueDateString).subtract(2, 'weeks'); break;
                    //     case 'Monthly'  :  potentialPreviousDueDate = moment(currentDueDateString).subtract(1, 'month');
                    //                        if (currentTask.repeatOnLastDayOfMonth === 'yes' || currentTask.repeatOnLastDayOfMonth === true) {
                    //                            potentialPreviousDueDate = potentialPreviousDueDate.endOf('month');
                    //                        }
                    //                        break;
                    //     case 'Yearly'   :  potentialPreviousDueDate = moment(currentDueDateString).subtract(1, 'year');
                    //                        if (currentTask.repeatOnLastDayOfMonth === 'yes' || currentTask.repeatOnLastDayOfMonth === true) {
                    //                            potentialPreviousDueDate = potentialPreviousDueDate.endOf('month');
                    //                        }
                    //                        break;
                    //     default         :  potentialPreviousDueDate = moment(currentDueDateString);
                    //                        console.warn(`Unknown repeat frequency "${currentTask.repeatFrequency}" when reverting task ${payload.id}`);
                    //                        break;
                    // }

                    // 4. Check if the task was completed ON its due date - REMOVED THIS CHECK AS IT WAS FLAWED
                    // ... removed check logic ...

                    // console.log(`Calculated previous due date (the correct one to revert to): ${potentialPreviousDueDate.format('MM/DD/YYYY')}`);

                    // 5. Modify payload based on check - SIMPLIFIED: Just clear completion, keep original date from payload
                    // finalPayload.dueAt = potentialPreviousDueDate.toDate(); // Use the calculated original due date
                    // finalPayload.dueAtStr = potentialPreviousDueDate.format('MM/DD/YYYY');

                    // Ensure dueAt from payload is converted to Date object if it's not already
                    if (finalPayload.dueAt && typeof finalPayload.dueAt.toDate !== 'function') {
                       // Assuming payload.dueAt is a string or JS Date already from TodoItem
                       finalPayload.dueAt = moment(finalPayload.dueAt).toDate();
                    }
                    // dueAtStr from payload is already correct

                    // 6. Clear completion fields
                    finalPayload.completedAt = null;
                    finalPayload.completedByEmpID = null;

                } else {
                   // Not reverting a recurring task, proceed as normal (might still be other updates)
                   // console.log(`Updating task ${payload.id} (Not a recurring task revert)`);
                }
                // --- END MODIFICATION ---

                // Determine office timezone and date format for potential completion date
                const officeTimezone = officeZoneOf(rootState);
                const dateFormat = officeTimezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

                // *** NEW: Handle completedAt normalization if task is being marked complete ***
                if (finalPayload.status === 'COMPLETED' && !currentTask?.completedAt) { // Check if marking complete NOW
                    const startOfTodayOfficeTZ = moment.tz(officeTimezone).startOf('day');
                    finalPayload.completedAt = startOfTodayOfficeTZ.toDate();
                    // completedAtStr is not typically stored, but if needed:
                    // finalPayload.completedAtStr = startOfTodayOfficeTZ.format(dateFormat);
                    finalPayload.completedByEmpID = finalPayload.completedByEmpID || rootState.customer?.userID; // Ensure completer ID is set
                    
                    // *** SUBTASK CASCADE COMPLETION ***
                    // When parent task is completed, mark all subtasks as completed too
                    // Check both finalPayload.subtasks (from detail sidebar) and currentTask.subtasks (from list checkbox click)
                    const subtasksToComplete = finalPayload.subtasks || currentTask?.subtasks;
                    if (subtasksToComplete && Array.isArray(subtasksToComplete) && subtasksToComplete.length > 0) {
                        finalPayload.subtasks = subtasksToComplete.map(subtask => ({
                            ...subtask,
                            status: 'COMPLETED',
                            completedAt: subtask.status === 'COMPLETED' ? subtask.completedAt : startOfTodayOfficeTZ.toDate(),
                            completedByEmpID: subtask.status === 'COMPLETED' ? subtask.completedByEmpID : (finalPayload.completedByEmpID || rootState.customer?.userID)
                        }));
                        // console.log(`Cascaded completion to ${finalPayload.subtasks.length} subtasks`);
                    }
                    // *** END SUBTASK CASCADE COMPLETION ***
                }
                // *** END NEW ***

                // Use finalPayload for Firestore and Algolia updates
                const taskRef = doc(rootState.db, "tasks", finalPayload.id);

                // Handle monthly recurring tasks with preserved day of month
                if (finalPayload.repeatFrequency === 'Monthly' && finalPayload.repeatOnDay) {
                    // If this is a task with a preserved day of month, ensure it's not modified
                    if (finalPayload._preserveRepeatOnDay) {
                        // Remove the special property before saving to Firestore
                        delete finalPayload._preserveRepeatOnDay;
                    }
                }

                // Explicitly include tags in the update data, ensuring it's an array
                const dataToUpdate = {
                    ...finalPayload, // Spread the rest of the payload
                    tags: Array.isArray(finalPayload.tags) ? finalPayload.tags : []
                };
                // Remove id from the update data if it was spread from finalPayload
                delete dataToUpdate.id;

                // console.log(`Updating Firestore task ${finalPayload.id} with data:`, dataToUpdate); // Keep this one? Maybe comment for now.

                // *** CORRECTED: Use updateDoc for partial updates instead of setDoc (overwrite) ***
                await updateDoc(taskRef, dataToUpdate); // Pass the prepared data
                // Original problematic line: await setDoc(taskRef, finalPayload);
                // *** END CORRECTION ***

                if (getters.isTaskDueToday(finalPayload)) { // Check finalPayload
                    localStorage.removeItem('notificationsLastCleared');
                    dispatch('notifications/setNotificationLastCleared', null, { root: true });
                }
                // dispatch('alert/setLoaded2', true, { root: true }); // REMOVED: Stop triggering global success alert

                // Update Algolia with finalPayload (non-blocking - task update succeeds even if Algolia fails)
                try {
                    let searchRecord = {
                        ...finalPayload,
                        objectID: finalPayload.id
                    };
                    // Convert Timestamps back for Algolia if necessary
                    if (searchRecord.dueAt && typeof searchRecord.dueAt.toDate === 'function') {
                       searchRecord.dueAt = searchRecord.dueAt.toDate().toISOString();
                    }
                     if (searchRecord.createdAt && typeof searchRecord.createdAt.toDate === 'function') {
                       searchRecord.createdAt = searchRecord.createdAt.toDate().toISOString();
                    }
                    // CompletedAt is null/deleted in finalPayload when reverting
                    delete searchRecord.completedAt;

                    await tasksIndex.partialUpdateObject(searchRecord, { createIfNotExists: false }); // Use partialUpdateObject
                } catch (algoliaErr) {
                    console.warn('Algolia: Error updating task in index (non-critical):', algoliaErr.message);
                }

                // No need to commit mutation here as Firestore listener should update state
                // commit('setTask', finalPayload); // Commit the final payload to state
                
                // NEW: Ensure tags exist in task-tags collection
                if (finalPayload.tags && Array.isArray(finalPayload.tags) && finalPayload.siteID) {
                    for (const tag of finalPayload.tags) {
                        if (tag && tag.trim()) {
                            try {
                                await dispatch('addSiteTaskTag', { 
                                    siteID: finalPayload.siteID, 
                                    tagName: tag.trim() 
                                });
                            } catch (error) {
                                console.error(`Failed to add tag "${tag}" to task-tags collection:`, error);
                                // Don't fail the whole operation if tag sync fails
                            }
                        }
                    }
                }

            } catch(err) {
                console.error('Error updating task:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw(err);
            }
        },

        /**
         * Lightweight action to auto-save subtasks without triggering full task update
         * Used for auto-saving subtask changes (add, delete, toggle status, reorder)
         */
        async updateTaskSubtasks({ rootState }, { taskId, subtasks }) {
            try {
                const taskRef = doc(rootState.db, "tasks", taskId);

                // Prepare subtasks data for Firestore
                const subtasksData = subtasks.map((st, index) => ({
                    id: st.id,
                    content: st.content,
                    status: st.status || 'INCOMPLETE',
                    sortOrder: index,
                    createdAt: st.createdAt || new Date(),
                    ...(st.completedAt && { completedAt: st.completedAt }),
                    ...(st.completedByEmpID && { completedByEmpID: st.completedByEmpID })
                }));

                // Only update the subtasks field
                await updateDoc(taskRef, { subtasks: subtasksData });

                // console.log(`Auto-saved ${subtasksData.length} subtasks for task ${taskId}`);
            } catch (err) {
                console.error('Error auto-saving subtasks:', err);
                throw err;
            }
        },

        async deleteTask({ commit, rootState, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                const taskRef = doc(rootState.db, "tasks", payload.id);

                // Check if this is a repeat deletion or just marking as deleted
                if (payload.permanentDelete) {
                    // Actually delete the document for repeated tasks when we want to delete all instances
                    await deleteDoc(taskRef);
                    dispatch('alert/setLoaded', true, {root: true});
                } else {
                    // Just mark as deleted (the standard behavior)
                    await updateDoc(taskRef, {
                        deleted: true,
                        deletedBy: payload.deletedBy || '',
                        deletedOn: payload.deletedOn || moment().toDate()
                    });
                    dispatch('alert/setLoaded', true, {root: true});
                }

                try {
                    await tasksIndex.deleteObject(payload.id);
                } catch (error) {
                    // ignoring this error since there's not much I can do about it
                    console.log('Error deleting task from search index:', error);
                }
            } catch(err) {
                console.error('Error deleting task:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async deleteAllCompletedTasks({ commit, rootState, dispatch, state }, payload) {
            dispatch('alert/setLoading', true, {root: true});
            let backendPromise;
            let algoliaPromise = Promise.resolve(); // Default to resolved promise if no tasks or Algolia fails

            try {
                // 1. Identify completed task IDs from the current state
                const allTasks = _.uniqBy([
                    ...(state.myCreatedTasks || []),
                    ...(state.assignedToMeTasks || []),
                    ...(state.myEmployeesTasks || [])
                ], 'id');

                const completedTaskIds = allTasks
                    .filter(task => task.status === 'COMPLETED')
                    .map(task => task.id);

                // 2. Prepare parallel promises
                // M-DT: /delete-completed-tasks now requires a Firebase ID token server-side.
                const _idToken = await getFirebaseIdToken(rootState && rootState.getAuth ? rootState.getAuth() : null);
                const _authHeaders = {};
                if (_idToken) _authHeaders.Authorization = `Bearer ${_idToken}`;
                backendPromise = axios.post(`/delete-completed-tasks`, payload, { crossdomain: true, headers: _authHeaders });

                if (completedTaskIds.length > 0) {
                    algoliaPromise = tasksIndex.deleteObjects(completedTaskIds)
                        .catch(algoliaError => {
                            // 3a. Log Algolia errors but don't throw
                            console.error('Algolia: Error deleting completed tasks from index:', algoliaError);
                            // Allow the flow to continue by returning a resolved promise or specific value
                            return { algoliaSkipped: true, error: algoliaError };
                        });
                }

                // 3. Execute in parallel and wait for both
                const [algoliaResult, backendResult] = await Promise.allSettled([algoliaPromise, backendPromise]);

                // 4. Handle Backend Result (Primary)
                if (backendResult.status === 'rejected') {
                    throw backendResult.reason;
                }

                dispatch('alert/setLoaded', true, {root: true});

            } catch(err) {
                // This catches errors specifically thrown from the backend promise
                console.error('Error during deleteAllCompletedTasks:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err.message || 'Failed to delete completed tasks.', { root: true });
                // No need to throw again from here unless needed upstream
            } finally {
                 // Ensure loading is always turned off
                dispatch('alert/setLoading', false, {root: true});
            }
        },

        async deleteThisInstanceOfRepeatedTask({ commit, rootState, getters, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                payload.task.status = 'INCOMPLETE';
                let nextDateMoment = getters.getNextDueDate(payload.task);

                // *** FIX: Normalize nextDateMoment to start of day in task's timezone ***
                const taskTimezone = payload.task.timezone || officeZoneOf(rootState); // Get task timezone or default
                const normalizedNextDate = nextDateMoment.clone().tz(taskTimezone).startOf('day');
                // *** END FIX ***

                payload.task.createdAt = moment(payload.task.createdAt).toDate(); // Keep original createdAt
                // *** FIX: Use normalized date for dueAt and dueAtStr ***
                payload.task.dueAt = normalizedNextDate.toDate();
                payload.task.dueAtStr = normalizedNextDate.format(payload.clientDateFormat); // Use clientDateFormat from payload
                // *** END FIX ***

                // Clear completion fields explicitly when deleting instance (making it incomplete)
                payload.task.completedAt = null;
                payload.task.completedByEmpID = null;

                const taskRef = doc(rootState.db, "tasks", payload.task.id);
                // Prepare payload for updateDoc, removing ID
                const { id, ...updateData } = payload.task;
                await updateDoc(taskRef, updateData); // Use updateDoc and pass data without id
                dispatch('alert/setLoaded', true, {root: true});

                // Algolia update (non-blocking)
                try {
                    let searchRecord = {
                        ...payload.task,
                        objectID: payload.task.id
                    };
                    await tasksIndex.partialUpdateObject(searchRecord);
                } catch (algoliaErr) {
                    console.warn('Algolia: Error updating repeated task in index (non-critical):', algoliaErr.message);
                }
            } catch(err) {
                console.error('Error deleting current instance of repeated task:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async getTasksTaggedWithPatients({ commit, rootState, dispatch}, payload) {
            const {siteID, patientsToLookFor } = payload;
            try {
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(tasksRef, where('siteID', '==', siteID), where('deleted', '!=', true), where('taggedPatients', 'array-contains-any', patientsToLookFor));
                const unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    commit('resetTasksTaggedWithPatients');
                    const taggedTasks = [];
                    tasksSnapshot.forEach((task) => {
                        const tempTask = task.data();
                        tempTask.id = task.id;
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- Robust completedAt handling ---
                        let completedDate = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDate = moment(tempTask.completedAt.toDate()); }
                            else { completedDate = moment(tempTask.completedAt); }
                        }
                        tempTask.completedAt = completedDate ? completedDate.format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- End robust handling ---
                        taggedTasks.push(tempTask);
                    });
                    commit('setTasksTaggedWithPatients', taggedTasks);
                }, { source: 'tasks/taggedWithPatients' });
                commit('customer/addFirestoreListener', unsubscribe, {root: true});
                // Hand the unsubscribe back so the CALLER can tear this listener down.
                // PracticePulse.vue re-dispatches this action from its allPatNums watcher on
                // every check-in/out change; without this return there was nothing to tear
                // down, so listeners accumulated for the whole session, outlived the page,
                // and a stale one could overwrite the SHARED slot
                // `state.tasks.tasksTaggedWithPatients` with an older patient set.
                // Same contract as the singular getTasksTaggedWithPatient below.
                return unsubscribe;
            } catch (error) {
                console.log(error);
                // Explicit null (not undefined) so a caller can tell "subscribe failed" from
                // "here is your unsubscribe" without a typeof dance.
                return null;
            }
        },

        async getTasksTaggedWithPatient({ commit, rootState, dispatch}, payload) {
            // `onError` (optional): called with (err, { denied }) so the caller (PatientTasksCard) can
            // show "couldn't load" instead of an empty "No open tasks" state.
            const {siteID, patientToLookFor, onError } = payload;
            try {
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(tasksRef, where('siteID', '==', siteID), where('deleted', '!=', true), where('taggedPatients', 'array-contains', patientToLookFor));
                const unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    const taggedTasks = [];
                    tasksSnapshot.forEach((task) => {
                        const tempTask = task.data();
                        //console.log('tempTask', tempTask);
                        tempTask.id = task.id;
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- Robust completedAt handling ---
                        let completedDate = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDate = moment(tempTask.completedAt.toDate()); }
                            else { completedDate = moment(tempTask.completedAt); }
                        }
                        tempTask.completedAt = completedDate ? completedDate.format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- End robust handling ---
                        taggedTasks.push(tempTask);
                    });
                    //console.log('taggedTasks', taggedTasks);
                    commit('setTasksTaggedWithPatient', taggedTasks);
                }, { source: 'tasks/taggedWithPatient', onError });
                commit('customer/addFirestoreListener', unsubscribe, {root: true});
                return unsubscribe;
            } catch (error) {
                console.log(error);
            }
        },

        async getTaskDetails({ commit, rootState, dispatch}, payload) {
            try {
                const taskRef = doc(rootState.db, 'tasks', payload.taskID);
                const taskDoc = await getDoc(taskRef);
                if(taskDoc.exists()) {
                    const tempTask = taskDoc.data();
                    tempTask.id = taskDoc.id;

                    // --- CORRECTED DATE HANDLING --- 
                    // Store actual JS Date objects, not formatted strings
                    const officeTimezone = officeZoneOf(rootState);
                    const dateFormat = officeTimezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

                    // Convert Timestamps to JS Dates
                    const dueAtDate = tempTask.dueAt?.toDate() || null;
                    const createdAtDate = tempTask.createdAt?.toDate() || null;
                    const updatedAtDate = tempTask.updatedAt?.toDate() || null;
                    const completedAtDate = tempTask.completedAt?.toDate() || null;
                    const lastActivityDate = tempTask.lastActivity?.toDate() || null;

                    // Assign JS Dates to the task object
                    tempTask.dueAt = dueAtDate;
                    tempTask.createdAt = createdAtDate;
                    tempTask.updatedAt = updatedAtDate;
                    tempTask.completedAt = completedAtDate;
                    tempTask.lastActivity = lastActivityDate;

                    // Also generate and store the correct dueAtStr based on the normalized dueAt Date
                    if (dueAtDate) {
                        const normalizedDueMoment = moment.tz(dueAtDate, officeTimezone).startOf('day');
                        tempTask.dueAtStr = normalizedDueMoment.format(dateFormat);
                    } else {
                        tempTask.dueAtStr = ''; // Or null, depending on desired handling
                    }
                    // --- END CORRECTED DATE HANDLING ---

                    // Explicitly handle tags and wikiPages arrays
                    tempTask.tags = Array.isArray(taskDoc.data().tags) ? [...taskDoc.data().tags] : [];
                    tempTask.wikiPages = Array.isArray(taskDoc.data().wikiPages) ? [...taskDoc.data().wikiPages] : [];

                    const commentsRef = collection(rootState.db, 'comments');
                    const q = query(commentsRef, where('taskID', '==', payload.taskID), orderBy("createdAt"));
                    const commentsSnapshot = await getDocs(q);
                    const tempComments = [];
                    commentsSnapshot.forEach((comment) => {
                        const tempComment = comment.data();
                        tempComment.id = comment.id;
                        tempComment.createdAt = tempComment.createdAt ? moment(tempComment.createdAt.toDate()) : '';
                        tempComments.push(tempComment);
                    });
                    tempTask.comments = tempComments;
                    Vue.set(tempTask, 'comments', tempComments);
                    commit('setTask', tempTask);
                    return tempTask;
                } else {
                    // Task not found, simply return null. The caller will handle the UI notification.
                    return null;
                }
            } catch(err) {
                // Log errors, but don't dispatch alerts here. Return null.
                console.error('Error while getting task details:', err);
                // Return null to indicate an error occurred or task wasn't found.
                return null;
            }
        },

        async getTasksForSite({ commit, rootState, dispatch, state}, payload) {
            let safetyTimeoutId = null;
            const startTime = performance.now();

            // Safety mechanism - if the loading state is stuck for more than 5 seconds, force it to complete
            safetyTimeoutId = setTimeout(() => {
                commit('setTasksLoaded', true);
                dispatch('alert/setLoading', false, {root: true});
            }, 5000);

            try {
                commit('setTasksLoaded', false);
                commit('updateTasksLoadingPhase', 'initial');
                dispatch('alert/setLoading', true, {root: true});
                
                // Start performance monitoring if enabled
                if (state.performanceFlags.enablePerformanceMonitoring) {
                    dispatch('startPerformanceMonitoring');
                }
                
                const tasksRef = collection(rootState.db, 'tasks');
                // *** ADDED: Filter for INCOMPLETE tasks at the query level ***
                const q = query(tasksRef, where('siteID', '==', payload.siteID), where('deleted', '!=', true), where('status', '==', 'INCOMPLETE'), orderBy("deleted"), orderBy("dueAt"));
                
                const unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    // Track performance metrics
                    const loadTime = performance.now() - startTime;
                    commit('updatePerformanceMetrics', {
                        initialLoadTime: loadTime,
                        totalTasksLoaded: tasksSnapshot.size
                    });

                    // Initialize task arrays for debounced updates
                    const createdTasks = [];
                    const assignedTasks = [];
                    const employeeTasks = [];

                    tasksSnapshot.forEach((task) => {
                        const taskData = task.data(); // Get data once
                        const tempTask = { ...taskData }; // Create a mutable copy

                        // Explicitly handle tags array
                        tempTask.tags = Array.isArray(taskData.tags) ? [...taskData.tags] : []; // Ensure tags is an array

                        tempTask.unreadCommentsList = taskData.unreadCommentsList || [];
                        tempTask.id = task.id;
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- Robust completedAt handling ---
                        let completedDate = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDate = moment(tempTask.completedAt.toDate()); }
                            else { completedDate = moment(tempTask.completedAt); }
                        }
                        tempTask.completedAt = completedDate ? completedDate.format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- End robust handling ---

                        // Collect tasks for debounced updates instead of immediate state mutation
                        if(tempTask.createdByEmpID === payload.employeeID) {
                            createdTasks.push(tempTask);
                        }

                        // Check assignees carefully, ensuring tempTask.assignees exists
                        const isAssigned = tempTask.assignees && Array.isArray(tempTask.assignees) && tempTask.assignees.includes(payload.employeeID);
                        if(tempTask.assignedToEmpID === payload.employeeID || isAssigned) {
                            assignedTasks.push(tempTask);
                        }

                        // Check if it belongs in MyEmployeesTasks (adjust logic if needed)
                        // This logic might need review depending on exact requirements
                        if(tempTask.createdByEmpID !== payload.employeeID && !isAssigned && tempTask.assignedToEmpID !== payload.employeeID) {
                            employeeTasks.push(tempTask);
                        }
                    });

                    // Use debounced updates instead of immediate state mutations
                    dispatch('debouncedUpdateMyCreatedTasks', createdTasks);
                    dispatch('debouncedUpdateAssignedToMeTasks', assignedTasks);
                    dispatch('debouncedUpdateMyEmployeesTasks', employeeTasks);

                    // Update loading phase and complete loading
                    commit('updateTasksLoadingPhase', 'complete');
                    
                    // Clear the safety timeout since we have data
                    clearTimeout(safetyTimeoutId);
                    commit('setTasksLoaded', true);
                    dispatch('alert/setLoading', false, {root: true});
                }, { source: 'tasks/forSite' });

                // Ensure we set tasks loaded to true even if there's no data
                setTimeout(() => {
                    commit('setTasksLoaded', true);
                    dispatch('alert/setLoading', false, {root: true});
                }, 1000);

                commit('customer/addFirestoreListener', unsubscribe, {root: true});

            } catch(err) {
                console.log('Error while getting tasks for site:', err);
                clearTimeout(safetyTimeoutId);
                commit('setTasksLoaded', true);
                dispatch('alert/setLoading', false, {root: true});
                throw err;
            }
        },

        /**
         * The Ctrl+K search's tasks when the Tasks page has not loaded them: ONE read (getDocs) of the site's
         * open tasks — the same query as getTasksForSite — with no listener, no loading overlay, and nothing
         * written into the lists the Tasks page uses. Rows go to state.searchTasks only.
         * Guarded by cypress/node-tests/tasks-search-load.test.js (query clauses must equal getTasksForSite's).
         */
        async getTasksForSearch({ commit, rootState }, payload) {
            const siteID = payload && payload.siteID;
            if (!siteID) return;
            commit('startSearchTasks');
            try {
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(tasksRef, where('siteID', '==', siteID), where('deleted', '!=', true), where('status', '==', 'INCOMPLETE'), orderBy("deleted"), orderBy("dueAt"));
                const snapshot = await getDocs(q);
                const docs = [];
                snapshot.forEach(d => docs.push(d));
                commit('setSearchTasks', { siteID, rows: searchRowsOf(docs) });
            } catch (err) {
                console.error('Error while reading tasks for the search:', err);
                commit('failSearchTasks');
            }
        },

        async getTasksForEmployee({ commit, rootState, dispatch, state}, payload) {
            let safetyTimeoutId = null;
            const startTime = performance.now();

            // Safety mechanism - if the loading state is stuck for more than 5 seconds, force it to complete
            safetyTimeoutId = setTimeout(() => {
                commit('setTasksLoaded', true);
                dispatch('alert/setLoading', false, {root: true});
            }, 5000);

            try {
                dispatch('alert/setLoading', true, {root: true});
                commit('setTasksLoaded', false);
                commit('updateTasksLoadingPhase', 'initial');
                
                const tasksRef = collection(rootState.db, 'tasks');
                // *** ADDED: Filter for INCOMPLETE tasks at the query level ***
                const q1 = query(tasksRef, where('siteID', '==', payload.siteID), where('deleted', '!=', true), where('status', '==', 'INCOMPLETE'), where('assignees', 'array-contains', payload.employeeID), orderBy("deleted"), orderBy("dueAt"));
                const unsubscribe1 = safeSnapshot(q1, (taskSnapshot) => {
                    // Use a temporary array to build the list for this snapshot
                    let assignedTasks = [];
                    taskSnapshot.forEach((task) => {
                        const taskData = task.data();
                        const tempTask = { ...taskData };
                        tempTask.id = task.id;
                        // Explicitly handle tags
                        tempTask.tags = Array.isArray(taskData.tags) ? [...taskData.tags] : [];
                        tempTask.unreadCommentsList = taskData.unreadCommentsList || [];
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';

                        // --- Robust completedAt handling ---
                        let completedDate = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDate = moment(tempTask.completedAt.toDate()); }
                            else { completedDate = moment(tempTask.completedAt); }
                        }
                        tempTask.completedAt = completedDate ? completedDate.format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- End robust handling ---

                        assignedTasks.push(tempTask);
                    });
                    // Use debounced updates instead of immediate state mutation
                    dispatch('debouncedUpdateAssignedToMeTasks', assignedTasks);
                }, { source: 'tasks/forEmployee/assigned' });

                // *** ADDED: Filter for INCOMPLETE tasks at the query level ***
                const q2 = query(tasksRef, where('siteID', '==', payload.siteID), where('createdByEmpID', '==', payload.employeeID), where('deleted', '!=', true), where('status', '==', 'INCOMPLETE'), orderBy("deleted"), orderBy("dueAt"));
                const unsubscribe2 = safeSnapshot(q2, (taskSnapshot) => {
                    // Use a temporary array to build the list for this snapshot
                    let createdTasks = [];
                    taskSnapshot.forEach((task) => {
                        const taskData = task.data();
                        const tempTask = { ...taskData };
                        tempTask.id = task.id;
                         // Explicitly handle tags
                        tempTask.tags = Array.isArray(taskData.tags) ? [...taskData.tags] : [];
                        tempTask.unreadCommentsList = taskData.unreadCommentsList || [];
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';

                        // --- Robust completedAt handling ---
                        let completedDateQ2 = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDateQ2 = moment(tempTask.completedAt.toDate()); }
                            else { completedDateQ2 = moment(tempTask.completedAt); }
                        }
                        tempTask.completedAt = completedDateQ2 ? completedDateQ2.format('MM/DD/YYYY h:mm:ss a') : '';
                        // --- End robust handling ---

                        createdTasks.push(tempTask);
                    });
                    
                    // Use debounced updates instead of immediate state mutation
                    dispatch('debouncedUpdateMyCreatedTasks', createdTasks);

                    // Track performance metrics and complete loading
                    const loadTime = performance.now() - startTime;
                    commit('updatePerformanceMetrics', {
                        initialLoadTime: loadTime,
                        totalTasksLoaded: createdTasks.length
                    });
                    commit('updateTasksLoadingPhase', 'complete');
                    
                    // Set loading to complete after we've processed the created tasks
                    clearTimeout(safetyTimeoutId);
                    commit('setTasksLoaded', true);
                    dispatch('alert/setLoading', false, {root: true});
                }, { source: 'tasks/forEmployee/created' });

                // Ensure we set tasks loaded to true even if there's no data
                setTimeout(() => {
                    commit('setTasksLoaded', true);
                }, 1000);

                commit('customer/addFirestoreListener', unsubscribe1, {root: true});
                commit('customer/addFirestoreListener', unsubscribe2, {root: true});

            } catch(err) {
                console.log('Error while getting tasks for employee:', err);
                clearTimeout(safetyTimeoutId);
                commit('setTasksLoaded', true);
                dispatch('alert/setLoading', false, {root: true});
                throw err;
            }
        },

        async addUpdateFollowupTask( { commit, dispatch, rootState}, payload) {
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post('/addupdatefollowuptask', payload, { crossdomain: true });
                dispatch('alert/setLoading2', false, {root: true});
            } catch(err) {
                console.log('Error while adding and updating a followup task:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw(err);
            }
        },

        async editFollowupNote( { commit, dispatch, rootState}, payload) {
            try {
                dispatch('alert/setLoading2', true, {root: true});
                let response = await axios.post('/edit-followup-note', payload, { crossdomain: true });
                dispatch('alert/setLoading2', false, {root: true});
                dispatch('alert/setMessage2', 'Follow-up Note updated successfully!', {root: true});
            } catch(err) {
                console.log('Error while editing followup note:', err);
                dispatch('alert/setErrored2', true, {root: true});
                dispatch('alert/setMessage2', err, {root: true});
                throw(err);
            }
        },

        async updateInitialFollowupDate( { commit, dispatch, rootState}, payload) {
            try {
                let response = await axios.post('/update-initial-followup-date', payload, { crossdomain: true });
            } catch(err) {
                console.error('Error while updating initial followup date:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async removeInitialFollowupDate( { commit, dispatch, rootState}, payload) {
            try {
                let response = await axios.post('/remove-initial-followup-date', payload, { crossdomain: true });
            } catch(err) {
                console.error('Error while removing initial followup date:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async updateFollowupDate( { commit, dispatch, rootState}, payload) {
            try {
                let response = await axios.post('/update-followup-date', payload, { crossdomain: true });
            } catch(err) {
                console.error('Error while updating followup date:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async stopFollowups( { commit, dispatch, rootState}, payload) {
            try {
                let response = await axios.post('/stop-followups', payload, { crossdomain: true });
            } catch(err) {
                console.error('Error while trying to stop followups:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async resetFollowups( { commit, dispatch, rootState}, payload) {
            try {
                let response = await axios.post('/reset-followups', payload, { crossdomain: true });
            } catch(err) {
                console.error('Error while trying to reset followups:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw(err);
            }
        },

        async getCompletedTasksForSite({ commit, rootState, dispatch }, payload) {
            let safetyTimeoutId = null;

            // Safety mechanism - if the loading state is stuck for more than 5 seconds, force it to complete
            safetyTimeoutId = setTimeout(() => {
                // console.log('Safety timeout triggered in getCompletedTasksForSite');
                dispatch('alert/setLoading', false, { root: true }); // Assuming loading state is managed elsewhere or not needed here
            }, 5000);

            try {
                // dispatch('alert/setLoading', true, { root: true }); // Optional: manage loading state if needed
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(
                    tasksRef,
                    where('siteID', '==', payload.siteID),
                    where('deleted', '!=', true),
                    where('status', '==', 'COMPLETED'), // Filter for completed tasks (Corrected case)
                    orderBy("completedAt", "desc") // Optional: Order by completion date, descending
                );

                const unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    commit('resetCompletedTasksForSite'); // Reset before populating
                    const completedTasks = [];

                    tasksSnapshot.forEach((task) => {
                        const taskData = task.data();
                        const tempTask = { ...taskData };

                        tempTask.id = task.id;
                        // Format dates similarly to other actions
                        tempTask.createdAt = tempTask.createdAt ? moment(tempTask.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = tempTask.dueAt ? moment(tempTask.dueAt.toDate()).format('MM/DD/YYYY h:mm:ss a') : '';

                        // Robust completedAt handling
                        let completedDate = null;
                        if (tempTask.completedAt) {
                            if (typeof tempTask.completedAt.toDate === 'function') { completedDate = moment(tempTask.completedAt.toDate()); }
                            else { completedDate = moment(tempTask.completedAt); } // Handle cases where it might already be a JS Date or string
                        }
                        tempTask.completedAt = completedDate ? completedDate.format('MM/DD/YYYY h:mm:ss a') : '';

                        completedTasks.push(tempTask);
                    });

                    commit('setCompletedTasksForSite', completedTasks);

                    // Clear the safety timeout since we have data (or no data, but the query finished)
                    clearTimeout(safetyTimeoutId);
                    // dispatch('alert/setLoading', false, { root: true }); // Optional: manage loading state
                }, { source: 'tasks/completedForSite' });

                // Ensure loading state is cleared even if there's no initial data (if loading state is used)
                // setTimeout(() => {
                //     dispatch('alert/setLoading', false, { root: true });
                // }, 1000);

                commit('customer/addFirestoreListener', unsubscribe, { root: true });

            } catch (err) {
                console.error('Error while getting completed tasks for site:', err);
                clearTimeout(safetyTimeoutId);
                // dispatch('alert/setLoading', false, { root: true }); // Ensure loading is off on error
                // dispatch('alert/setErrored', true, { root: true }); // Optional: dispatch error state
                // dispatch('alert/setMessage', `Failed to get completed tasks: ${err.message}`, { root: true });
                throw err; // Re-throw if needed upstream
            }
        },

        /**
         * The Admin "Today" page's "Done today" tasks (design 3a, Phase 7): a live listener on the
         * site's tasks completed TODAY, newest first, into state.completedTasksToday. ONE listener at
         * a time — a new call stops the previous one first. Stop it with unsubscribeCompletedTasksToday.
         *
         * The query is getCompletedTasksForSite's (siteID ==, deleted != true, status == COMPLETED,
         * orderBy completedAt desc) plus `completedAt >= <start of today>`. completedAt is already that
         * query's orderBy field AND the inequality field, so the composite index it uses
         * (siteID ASC, status ASC, completedAt DESC, deleted DESC — teamio-serverless-api
         * firebase/firestore.indexes.json) covers this one too: no new index.
         *
         * The bound: see src/utils/tasks/completedToday.js — updateTask stamps completedAt as the
         * start of the day in `stampZone` (below, the SAME expression updateTask reads — which today
         * always falls back to New York), so the bound is the earlier of the office's midnight and
         * the start of the office's date in that zone.
         *
         * Known edge (documented, not fixed): updateTask only stamps completedAt when the stored task
         * has none (`!currentTask?.completedAt`); otherwise it writes the payload's completedAt. Every
         * completion path in the app (TodoItem, TaskDrawer, AdminTaskRow, TaskRow via completeTask.js)
         * sends completedAt = now, so a task reopened and completed again today still lands in range.
         * A future path that completes WITHOUT a completedAt in its payload on a task whose old
         * completedAt survived a reopen (TodoItem's reopen deletes it only locally) would keep the
         * OLD date and not show here.
         *
         * @param {{ siteID: string, timeZone: string }} payload timeZone = the site's GENERAL_TIMEZONE
         * @returns {Function|null} the unsubscribe function (null when nothing could be started)
         */
        subscribeCompletedTasksToday({ commit, rootState }, payload) {
            const stopped = unsubscribeCompletedTasksToday();
            if (stopped) commit('customer/removeFirestoreListener', stopped, { root: true });

            const siteID = payload && payload.siteID;
            if (!siteID || !rootState.db) {
                commit('setCompletedTasksToday', []);
                return null;
            }
            // updateTask now stamps completedAt at the start of the OFFICE day (officeZoneOf), but every
            // completion before the 2026-10-06 fix was stamped at New York midnight. Bounding by the
            // EARLIER of the two keeps those legacy stamps visible and never admits a correct stamp
            // from yesterday (src/utils/tasks/completedToday.js).
            const stampZone = TASK_STAMP_DEFAULT_ZONE;
            const since = completedTodayBound({ moment, officeZone: payload.timeZone, stampZone, now: new Date() });

            try {
                const tasksRef = collection(rootState.db, 'tasks');
                const q = query(
                    tasksRef,
                    where('siteID', '==', siteID),
                    where('deleted', '!=', true),
                    where('status', '==', 'COMPLETED'),
                    where('completedAt', '>=', since),
                    orderBy('completedAt', 'desc')
                );

                let unsubscribe = null;
                unsubscribe = safeSnapshot(q, (tasksSnapshot) => {
                    // A late snapshot of a listener that was replaced / stopped must not overwrite.
                    if (unsubscribe && completedTasksTodayUnsubscribe !== unsubscribe) return;
                    const completedTasks = [];
                    tasksSnapshot.forEach((task) => {
                        const tempTask = { ...task.data() };
                        tempTask.id = task.id;
                        // Formatted as getCompletedTasksForSite formats them.
                        const created = toMomentOrNull(tempTask.createdAt);
                        const due = toMomentOrNull(tempTask.dueAt);
                        const completed = toMomentOrNull(tempTask.completedAt);
                        tempTask.createdAt = created ? created.format('MM/DD/YYYY h:mm:ss a z') : '';
                        tempTask.dueAt = due ? due.format('MM/DD/YYYY h:mm:ss a') : '';
                        tempTask.completedAt = completed ? completed.format('MM/DD/YYYY h:mm:ss a') : '';
                        completedTasks.push(tempTask);
                    });
                    commit('setCompletedTasksToday', completedTasks);
                }, {
                    source: 'tasks/completedToday',
                    onError: (err) => {
                        console.error('Error in the completed-today tasks listener:', err);
                    }
                });

                completedTasksTodayUnsubscribe = unsubscribe;
                commit('customer/addFirestoreListener', unsubscribe, { root: true });
                return unsubscribe;
            } catch (err) {
                console.error('Error while subscribing to the tasks completed today:', err);
                commit('setCompletedTasksToday', []);
                return null;
            }
        },

        /** Stops the "Done today" tasks listener (if any) and clears state.completedTasksToday. */
        unsubscribeCompletedTasksToday({ commit }) {
            const stopped = unsubscribeCompletedTasksToday();
            if (stopped) commit('customer/removeFirestoreListener', stopped, { root: true });
            commit('setCompletedTasksToday', []);
        },

        // Performance optimization actions
        
        // Debounced task update functions (similar to conversations system)
        debouncedUpdateMyCreatedTasks: _.debounce(function({ commit, state }, tasks) {
            if (state.performanceFlags.enableMemoryCleanup) {
                if (tasks.length > state.performanceFlags.maxTasksPerCategory) {
                    tasks = tasks.slice(0, state.performanceFlags.maxTasksPerCategory);
                }
            }
            commit('resetMyCreatedTasks');
            tasks.forEach(task => commit('addTaskToMyCreatedTasks', task));
        }, 250),

        debouncedUpdateAssignedToMeTasks: _.debounce(function({ commit, state }, tasks) {
            if (state.performanceFlags.enableMemoryCleanup) {
                if (tasks.length > state.performanceFlags.maxTasksPerCategory) {
                    tasks = tasks.slice(0, state.performanceFlags.maxTasksPerCategory);
                }
            }
            commit('resetAssignedToMeTasks');
            tasks.forEach(task => commit('addTaskToAssignedToMeTasks', task));
        }, 250),

        debouncedUpdateMyEmployeesTasks: _.debounce(function({ commit, state }, tasks) {
            if (state.performanceFlags.enableMemoryCleanup) {
                if (tasks.length > state.performanceFlags.maxTasksPerCategory) {
                    tasks = tasks.slice(0, state.performanceFlags.maxTasksPerCategory);
                }
            }
            commit('resetMyEmployeesTasks');
            tasks.forEach(task => commit('addTaskToMyEmployeesTasks', task));
        }, 250),

        // Memory management action
        performMemoryCleanup({ commit, state }) {
            if (state.performanceFlags.enableMemoryCleanup) {
                commit('enforceTaskLimits');
            }
        },

        // Performance monitoring action
        startPerformanceMonitoring({ commit, state, dispatch }) {
            if (state.performanceFlags.enablePerformanceMonitoring) {
                // Set up periodic memory cleanup
                setInterval(() => {
                    dispatch('performMemoryCleanup');
                }, state.performanceFlags.memoryCleanupInterval);
            }
        },
    }
}
